import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Message} from '../../../contracts/message.interface';
import {Contact} from '../../../../contacts/contracts/contact.interface';
import {NgClass, NgIf} from '@angular/common';
import {MessageReplyComponent} from '../message-reply/message-reply.component';
import {MessageRendererComponent} from '../message-renderer/message-renderer.component';
import {Message as PrimeMessage} from 'primeng/message';
import {AuthService} from '../../../../auth/services/auth.service';
import {ConversationService} from '../../../services/conversation.service';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';

@Component({
  standalone: true,
  selector: 'app-message-bubble',
  imports: [
    NgClass,
    MessageReplyComponent,
    NgIf,
    MessageRendererComponent,
    PrimeMessage,
    FormsModule,
    Button,
  ],
  templateUrl: './message-bubble.component.html',
  styleUrl: './message-bubble.component.scss'
})
export class MessageBubbleComponent implements OnInit, OnDestroy {
  @Input() message!: Message;
  @Input() contact?: Contact | null;
  @Input() disableActions: boolean = false;
  @Output() replyClicked = new EventEmitter<Message>();
  @Output() replyContextClicked = new EventEmitter<Message>();

  menuOpen = false;
  noteOpen = false;
  notePreviewOpen = false;
  noteText = '';
  noteSaveStatus: 'idle' | 'saving' | 'saved' = 'idle';

  private justOpened = false;
  private noteSubject = new Subject<string>();
  private noteSub?: Subscription;

  constructor(
    private authService: AuthService,
    private conversationService: ConversationService,
  ) {}

  ngOnInit() {
    this.noteSub = this.noteSubject.pipe(
      debounceTime(800),
      distinctUntilChanged(),
      switchMap(note => {
        this.noteSaveStatus = 'saving';
        return this.conversationService.updateMessageNote(this.message.id!, note || null);
      }),
    ).subscribe(updated => {
      this.message.note = updated.note ?? null;
      this.noteSaveStatus = 'saved';
    });
  }

  ngOnDestroy() {
    this.noteSub?.unsubscribe();
  }

  get isSender(): boolean {
    return this.message.type === 'S';
  }

  get senderName(): string {
    if (this.isSender) {
      const op = this.message.operator;
      if (!op) {
        const me = this.authService.getCurrentUser();
        const parts = [me?.firstName, me?.lastName].filter(Boolean);
        return parts.length ? parts.join(' ') : (me?.email ?? 'Sconosciuto');
      }
      const parts = [op.firstName, op.lastName].filter(Boolean);
      return parts.length ? parts.join(' ') : 'Sconosciuto';
    } else {
      if (!this.contact) return 'Sconosciuto';
      const parts = [this.contact.firstName, this.contact.lastName].filter(Boolean);
      return parts.length ? parts.join(' ') : 'Sconosciuto';
    }
  }

  toggleMenu() {
    if (this.menuOpen) {
      this.menuOpen = false;
      return;
    }
    this.menuOpen = true;
    this.justOpened = true;
    setTimeout(() => this.justOpened = false, 0);
  }

  onReply() {
    this.menuOpen = false;
    this.replyClicked.emit(this.message);
  }

  onViewContext() {
    this.menuOpen = false;
    this.replyContextClicked.emit(this.message);
  }

  toggleNotePreview() {
    this.notePreviewOpen = !this.notePreviewOpen;
  }

  openNote() {
    this.menuOpen = false;
    this.noteText = this.message.note ?? '';
    this.noteSaveStatus = 'idle';
    this.noteOpen = true;
  }

  closeNote() {
    this.noteOpen = false;
  }

  onNoteInput(value: string) {
    this.noteSaveStatus = 'idle';
    this.noteSubject.next(value.trim());
  }

  onReplyContextClick(event: MouseEvent) {
    event.stopPropagation();
    this.replyContextClicked.emit(this.message);
  }

  @HostListener('document:click')
  closeMenu() {
    if (this.justOpened) return;
    this.menuOpen = false;
    this.notePreviewOpen = false;
  }
}
